/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import BottomPartForm from './BottomPartForm';
import openPaydSimpleModel from './Models/openPaydSimpleModel';
import { ReactComponent as LocalIcon } from 'assets/payments.svg';
import { ReactComponent as IbanIcon } from 'assets/payment-advanced.svg';
import i18nContext from 'components/i18n-context';
import { CURRENCY_TYPES, PAYMENT_METHOD, RECIPIENT_TYPES } from 'components/common/constants';
import { amountFormattedValue, formatSortCode, prepareFieldErrors, strToFloat } from 'services/utils';
import { IconButton } from 'uikit/IconButton/IconButton';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';

const LOCAL_TAB = 'LOCAL_ACCOUNT';
const IBAN_TAB = 'IBAN';

const OpenPaydSimpleForm = ({
  customerNumber,
  calculateCommission,
  checkTransfer,
  uploadDocuments,
  iban,
  setIban,
  setIsNonIban,
  ibanCredentials,
  ibanWarning,
  clearIbanCredentials,
  checkIban,
  isRepeatPayment,
  validationSchema,
  account,
  paymentMethod,
  resetPaymentMethods,
  uploadedFiles,
  commission,
  removePaymentFile,
  error,
  setError,
  isCommissionLoading,
  isLoading,
  isSuccess,
  currency,
  commonForm,
  isFormResetAfterAccountChanging,
  setIsPreparedTransactionsCreation,
  isForApprovalTransactionEdit,
  isUserCanCreatePreparedTransaction,
  isUserCanSendTransaction,
  validateForApprovalTransaction,
  paymentPurposes
}) => {
  const i18n = useContext(i18nContext);
  const model = openPaydSimpleModel;
  const serverFieldsErrors = prepareFieldErrors(i18n, error);
  const [disabled, setDisabled] = useState(true);
  const form = useFormik(validationSchema);
  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    validateField,
    validateForm,
    resetForm,
    setFieldValue,
    setErrors
  } = commonForm || form;
  const [activeTab, setActiveTab] = useState(currency === 'GBP' && !values.recipient_wallet ? LOCAL_TAB : IBAN_TAB);

  useEffect(() => {
    if (activeTab === LOCAL_TAB) {
      setIsNonIban(true);
    } else {
      setIsNonIban(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!iban && !ibanCredentials && !isRepeatPayment) {
      resetForm();
    } else {
      const asyncOperations = async () => {
        await handleIbanComplete(false);
      };

      asyncOperations();
    }
  }, [account.wallet_number]);

  useEffect(() => {
    if (isSuccess) {
      resetForm({});
      setDisabled(true);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (iban && !values.recipient_wallet) {
      setFieldValue('recipient_wallet', iban);
    }
  }, [iban, values.recipient_wallet]);

  useEffect(() => {
    if (paymentMethod === PAYMENT_METHOD.INTERNAL && activeTab === LOCAL_TAB) {
      setFieldValue('account_number', '');
      setFieldValue('sort_code', '');
      delete errors['account_number'];
      delete errors['sort_code'];
      setErrors(errors);
      setActiveTab(IBAN_TAB);
    }

    const asyncOperations = async () => {
      if (!isFormResetAfterAccountChanging) return;

      if (
        (isRepeatPayment && !values.recipient_wallet && activeTab === LOCAL_TAB) ||
        (!isRepeatPayment && activeTab === IBAN_TAB)
      ) {
        if (values.amount && paymentMethod) {
          await calculateCommission(customerNumber, {
            walletNumber: account?.wallet_number,
            paymentMethod: paymentMethod,
            amount: strToFloat(values?.amount?.toString() || ''),
            bankCountry: ibanCredentials?.bank_country || values.bank_country
          });
        }

        if (!isForApprovalTransactionEdit) {
          await handleCheckTransfer(undefined);
        }
      }
    };

    asyncOperations();
  }, [paymentMethod, isRepeatPayment, isFormResetAfterAccountChanging, activeTab]);

  const handleCheckTransfer = async (fieldName) => {
    if (fieldName) await validateField(fieldName);
    if (
      !serverFieldsErrors?.iban &&
      (values.recipient_wallet || (values.account_number && values.sort_code)) &&
      values.amount &&
      values.recipient_type &&
      values.comment &&
      (values.recipient_type === RECIPIENT_TYPES.COMPANY
        ? values.company_name
        : values.first_name && values.last_name) &&
      (ibanCredentials?.bank_country === 'AE' || values.bank_country === 'AE' ? values.payment_purpose : values.amount)
    ) {
      const errorsResult = await validateForm();

      if (!Object.keys(errorsResult).length) {
        await checkTransfer(
          customerNumber,
          model({
            providerTableData: values,
            data: { wallet: account, paymentMethod, ...ibanCredentials }
          })
        );
        setDisabled(false);
      }
    }
  };

  // Clear payment_purpose when bank_country changes from UAE to another country
  useEffect(() => {
    if (values.bank_country && values.bank_country !== 'AE' && values.payment_purpose) {
      setFieldValue('payment_purpose', '');
    }
  }, [values.bank_country]);

  const handleRecipientTypeChange = (name, data) => handleChange({ target: { name, value: data } });
  const onAmountIBANChange = (e) => {
    handleChange(e);
    if (e.target.name === 'recipient_wallet') {
      setIban(e.target.value);
    }
  };

  const handleActiveTab = (activeTab) => {
    if (ibanCredentials) clearIbanCredentials();
    if (error) setError(null);
    if (!paymentMethod) resetPaymentMethods();
    if (activeTab === LOCAL_TAB) {
      resetPaymentMethods();
      setFieldValue('recipient_wallet', '');
      delete errors['recipient_wallet'];
    }
    if (activeTab === IBAN_TAB) {
      setFieldValue('account_number', '');
      setFieldValue('sort_code', '');
      delete errors['account_number'];
      delete errors['sort_code'];
    }
    setErrors(errors);
    setActiveTab(activeTab);
  };

  const getCommissionValue = () => {
    if (!commission.value && !commission.currency) {
      return '0.00';
    }
    return `${commission.commissionDetails?.displayAmount || amountFormattedValue(commission.value)}`;
  };

  const handleIbanComplete = async (causedOnBlur) => {
    if (causedOnBlur) await validateField('recipient_wallet');
    values.recipient_wallet && (await checkIban(values.recipient_wallet));
    if (causedOnBlur && !isForApprovalTransactionEdit) await handleCheckTransfer(undefined);
  };

  const bottomPartProps = {
    values,
    errors,
    error,
    handleChange,
    handleSubmit,
    serverFieldsErrors,
    onAmountIBANChange,
    calculateCommission,
    handleCheckTransfer,
    getCommissionValue,
    currency,
    currencyPrecision: account?.currency_precision,
    paymentMethod,
    paymentProvider: account.transfer_provider,
    ibanCredentials,
    isSubmitDisabled:
      (isForApprovalTransactionEdit ? false : disabled) ||
      isLoading ||
      isCommissionLoading ||
      !!Object.keys(errors).length ||
      !!Object.keys(serverFieldsErrors).length ||
      !!error ||
      !paymentMethod,
    isLoading: isLoading || isCommissionLoading,
    uploadedFiles,
    uploadDocuments,
    removePaymentFile,
    customerNumber,
    currentWalletNumber: account?.wallet_number,
    commission,
    setIsPreparedTransactionsCreation,
    isForApprovalTransactionEdit,
    isUserCanCreatePreparedTransaction,
    isUserCanSendTransaction,
    validateForApprovalTransaction,
    model: openPaydSimpleModel,
    setError,
    paymentPurposes
  };

  const onSortCodeChange = (e) => {
    let inputValue = e.target.value;
    let numericValue = formatSortCode(inputValue);
    setFieldValue('sort_code', numericValue);
  };

  return (
    <>
      {currency === CURRENCY_TYPES.GBP && paymentMethod !== PAYMENT_METHOD.INTERNAL && (
        <div className={'navigation-payment'}>
          <IconButton
            className={'navigation-payment-button'}
            text={i18n.getMessage('transfer.divider.simpleForm')}
            active={activeTab === LOCAL_TAB}
            isDisabled={paymentMethod === PAYMENT_METHOD.INTERNAL}
            onClick={() => handleActiveTab(LOCAL_TAB)}
            Icon={LocalIcon}
          />
          <IconButton
            className={'navigation-payment-button'}
            text={i18n.getMessage('transfer.label.iban')}
            active={activeTab === IBAN_TAB}
            onClick={() => handleActiveTab(IBAN_TAB)}
            Icon={IbanIcon}
          />
        </div>
      )}
      {(activeTab === IBAN_TAB || account.currency === CURRENCY_TYPES.EUR) && (
        <div className={'inputs-wrapper'}>
          <Input
            isRequired={true}
            isDisabled={!!values.account_number || !!values.sort_code}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.recipientIban.label')}
            name={'recipient_wallet'}
            value={values.recipient_wallet}
            error={
              (!values.account_number && errors.recipient_wallet) ||
              serverFieldsErrors.iban ||
              serverFieldsErrors.recipientIban
            }
            warning={ibanWarning}
            onChange={onAmountIBANChange}
            onBlur={() => handleIbanComplete(true)}
            subText={
              ibanCredentials?.bank_name &&
              i18n.getMessage('transfer.form.bankName.subText', { bankName: ibanCredentials?.bank_name })
            }
          />
        </div>
      )}
      {currency === 'GBP' && activeTab === LOCAL_TAB && (
        <div className={'inputs-wrapper-evenly'}>
          <Input
            isRequired={true}
            isDisabled={
              currency === CURRENCY_TYPES.GBP && paymentMethod !== PAYMENT_METHOD.INTERNAL && !!values.recipient_wallet
            }
            autoComplete={'off'}
            name={'account_number'}
            label={i18n.getMessage('transfer.form.accountNumberGBP.label')}
            value={values.account_number}
            error={errors.account_number || serverFieldsErrors.account_number}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('account_number')}
          />
          <Input
            isRequired={true}
            isDisabled={!!values.recipient_wallet}
            autoComplete={'off'}
            name={'sort_code'}
            label={i18n.getMessage('transfer.form.sortCode.label')}
            value={values.sort_code}
            error={errors.sort_code || serverFieldsErrors.sort_code}
            onChange={onSortCodeChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('sort_code')}
            max={8}
          />
        </div>
      )}
      <div className={`inputs-wrapper ${values.recipient_type}`}>
        <InputDropDown
          label={i18n.getMessage('transfer.form.recipientType.label')}
          autoComplete={'off'}
          name={'recipient_type'}
          value={values.recipient_type}
          onChange={handleRecipientTypeChange}
          options={Object.values(RECIPIENT_TYPES).map((value) => ({ key: value, value }))}
        />
        {values.recipient_type === RECIPIENT_TYPES.COMPANY && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.companyName.label')}
            name={'company_name'}
            value={values.company_name}
            error={errors.company_name || serverFieldsErrors.companyName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('company_name')}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.firstName.label')}
            name={'first_name'}
            value={values.first_name}
            error={errors.first_name || serverFieldsErrors.firstName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('first_name')}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.lastName.label')}
            name={'last_name'}
            value={values.last_name}
            error={errors.last_name || serverFieldsErrors.lastName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('last_name')}
          />
        )}
      </div>
      <BottomPartForm {...bottomPartProps} />
    </>
  );
};

OpenPaydSimpleForm.propTypes = {
  customerNumber: PropTypes.string,
  calculateCommission: PropTypes.func.isRequired,
  checkTransfer: PropTypes.func.isRequired,
  uploadDocuments: PropTypes.func.isRequired,
  ibanCredentials: PropTypes.object,
  ibanWarning: PropTypes.string,
  clearIbanCredentials: PropTypes.func,
  iban: PropTypes.string,
  setIban: PropTypes.func,
  setIsNonIban: PropTypes.func,
  checkIban: PropTypes.func.isRequired,
  isRepeatPayment: PropTypes.bool,
  validationSchema: PropTypes.object,
  paymentMethod: PropTypes.string,
  resetPaymentMethods: PropTypes.func,
  account: PropTypes.object,
  commission: PropTypes.object,
  isCommissionLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  setError: PropTypes.func,
  uploadedFiles: PropTypes.array,
  removePaymentFile: PropTypes.func,
  commonForm: PropTypes.object,
  currency: PropTypes.string,
  isSuccess: PropTypes.bool,
  isFormResetAfterAccountChanging: PropTypes.bool,
  setIsPreparedTransactionsCreation: PropTypes.func,
  isForApprovalTransactionEdit: PropTypes.bool,
  isUserCanCreatePreparedTransaction: PropTypes.bool,
  isUserCanSendTransaction: PropTypes.bool,
  validateForApprovalTransaction: PropTypes.func,
  paymentPurposes: PropTypes.object
};

export default OpenPaydSimpleForm;
